"use client";
import { useEffect, useState, useRef } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import apiFetch from "@/lib/fetch";
import VenueCard from "@/components/views/list/card/VenueCard";
import { VenueLoading } from "@/components/views/list/venue-list/Venue";
import { useEventContext } from "@/context/EventContext";
import { SectionHeadingTitle } from "@/components/SectionTitle";
import MapIcon from "@heroicons/react/24/solid/MapIcon";
import { useTranslation } from "@/hooks/translationContext";
import { useUserLocation } from "@/context/UserLocationContext";

interface Props extends SearchProps {
  data: any;
}

export default function VenuesOfEventsClientSection(props: Props) {
  const [data, setData] = useState<any>(props.data);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { eventFilters } = useEventContext();

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && data?.length > 0) {
      const venuesToCalculate = data
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, data, calculateDistances]);

  const fetchVenues = async (page: number, newData: boolean) => {
    let url = `event/venues?index=${page}`;

    if (props.service) url += `&service=${props.service}`;
    if (props.district) url += `&district=${props.district}`;
    if (props.venue) url += `&venue=${props.venue}`;
    if (eventFilters.service) url += `&eventService=${eventFilters.service}`;
    if (eventFilters.date) url += `&date=${eventFilters.date}`;

    apiFetch({ route: url }).then((res) => {
      setIsLoading(false);
      setHasMore(true);

      if (res?.length === 0) setHasMore(false);

      setData((prev: any) => {
        const response = newData
          ? prev.concat(res)
          : res;

        return response;
      });
    });
  };

  const next = (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    fetchVenues(page, true);
  };

  useEffect(() => {
    fetchVenues(0, false);
  }, [eventFilters]);

  if (!isLoading && data?.length == 0) return <></>;

  return (
    <div className="border-t border-stone-50 py-4">
      <EventTitle />

      <InfiniteScroll
        skeletonElementId="venue-card-skeleton"
        isLoading={isLoading}
        hasMore={hasMore}
        loading={<VenueLoading count={6} />}
        next={next}
        dataLength={data.response?.length || 0}
      >
        <div className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-3 gap-5">
          {Array.isArray(data) &&
            data?.map((venue: Venue.List, index: number) => {
              const distance = venueDistances.get(venue.id);
              return (
                <VenueCard titleHeadingLevel="h3" key={index} distance={distance} {...venue} />
              );
            })}
        </div>
      </InfiniteScroll>
    </div>
  );
}

function EventTitle() {
  const { t } = useTranslation()

  const title = t("text-menu-venue-filter", {
    defaultValue: "Mekanlar",
  });

  return <SectionHeadingTitle title={title} icon={MapIcon} />;
}
